import React, { useState } from "react";
import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface RatingProps {
  value: number;
  max?: number;
  size?: "sm" | "md" | "lg";
  color?: string;
  readOnly?: boolean;
  onChange?: (rating: number) => void;
  showValue?: boolean;
}

const RatingComponent: React.FC<RatingProps> = ({
  value,
  max = 5,
  size = "md",
  color = "text-yellow-400",
  readOnly = true,
  onChange,
  showValue = false,
}) => {
  const [hoverRating, setHoverRating] = useState(0);
  const [localValue, setLocalValue] = useState(value);

  // Determine star size based on prop
  const starSizeClass = {
    sm: "w-3 h-3",
    md: "w-5 h-5",
    lg: "w-6 h-6",
  }[size];

  const handleStarClick = (rating: number) => {
    if (readOnly) return;
    setLocalValue(rating);
    onChange?.(rating);
  };

  const stars = Array.from({ length: max }, (_, i) => {
    const starNumber = i + 1;
    const isFilled = readOnly 
      ? starNumber <= value 
      : starNumber <= (hoverRating || localValue);
    
    const halfFilled = !readOnly 
      ? false 
      : Math.ceil(value) === starNumber && value % 1 !== 0;
    
    return (
      <button
        key={i}
        type="button"
        className={cn(
          "rounded-md focus:outline-none focus:ring-1 focus:ring-primary",
          readOnly ? "cursor-default" : "cursor-pointer hover:scale-110 transition-transform"
        )}
        onClick={() => handleStarClick(starNumber)}
        onMouseEnter={() => !readOnly && setHoverRating(starNumber)}
        onMouseLeave={() => !readOnly && setHoverRating(0)}
        disabled={readOnly}
        aria-label={`Rate ${starNumber} out of ${max}`}
      >
        <Star
          className={cn(
            starSizeClass,
            halfFilled 
              ? "text-gray-300 relative before:absolute before:top-0 before:left-0 before:w-1/2 before:h-full before:overflow-hidden before:text-yellow-400" 
              : isFilled
              ? color
              : "text-gray-300"
          )}
          fill={isFilled ? "currentColor" : "none"}
        />
      </button>
    );
  });

  return (
    <div className="flex items-center">
      <div className="flex">{stars}</div>
      {showValue && (
        <span className={cn("ml-2 font-medium", color)}>
          {value.toFixed(1)}
        </span>
      )}
    </div>
  );
};

// Review component to show with the rating
interface ReviewProps {
  rating: number;
  reviewer: {
    name: string;
    avatar?: string;
  };
  date: string;
  content: string;
}

export const Review: React.FC<ReviewProps> = ({
  rating,
  reviewer,
  date,
  content,
}) => {
  return (
    <div className="space-y-2 py-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {reviewer.avatar ? (
            <img
              src={reviewer.avatar}
              alt={reviewer.name}
              className="w-8 h-8 rounded-full"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-sm font-medium">
                {reviewer.name.charAt(0)}
              </span>
            </div>
          )}
          <span className="font-medium">{reviewer.name}</span>
        </div>
        <span className="text-sm text-gray-500">{date}</span>
      </div>
      <div className="flex items-center">
        <RatingComponent value={rating} readOnly size="sm" />
      </div>
      <p className="text-gray-700">{content}</p>
    </div>
  );
};

export default RatingComponent; 